import * as actionTypes from './actionTypes';
import { TaskActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Task } from '../../domain/Task';
import { ListResults } from '../../common/List/List';

export type TaskStateType = {
  tasksList: ListResults<Task> | null;
  tasksLoading: boolean;
  tasksError: HttpError;
  tasksListUpdateNeeded: boolean;
  taskCreateLoading: boolean;
  taskCreateError: HttpError;
  taskCreateSuccess: boolean;
  createdTask: Task | null;
  taskUpdateLoading: boolean;
  taskUpdateError: HttpError;
  taskUpdateSuccess: boolean;
  taskDeleteLoading: boolean;
  taskDeleteError: HttpError;
  taskDeleteSuccess: boolean;
  task: Task | null;
  taskLoading: boolean;
  taskError: HttpError;
  allTasks: Task[];
  allTasksLoading: boolean;
  allTasksError: HttpError;
};

export type TaskActionType = TaskStateType & {
  type: TaskActionTypes;
};

export const initialState: TaskStateType = {
  tasksList: null,
  tasksLoading: true,
  tasksError: null,
  tasksListUpdateNeeded: false,
  taskCreateLoading: false,
  taskCreateError: null,
  taskCreateSuccess: false,
  createdTask: null,
  taskUpdateLoading: false,
  taskUpdateError: null,
  taskUpdateSuccess: false,
  taskDeleteLoading: false,
  taskDeleteError: null,
  taskDeleteSuccess: false,
  task: null,
  taskLoading: false,
  taskError: null,
  allTasks: [],
  allTasksError: null,
  allTasksLoading: false,
};

const fetchTasksStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  tasksLoading: true,
});

const fetchTasksSuccess = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  tasksList: action.tasksList,
  tasksLoading: false,
  tasksError: null,
  tasksListUpdateNeeded: false,
});

const fetchTasksFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  tasksError: action.tasksError,
  tasksLoading: false,
});

const fetchAllTasksStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  allTasksLoading: true,
});

const fetchAllTasksSuccess = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  allTasks: action.allTasks,
  allTasksLoading: false,
  allTasksError: null,
});

const fetchAllTasksFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  allTasksError: action.allTasksError,
  allTasksLoading: false,
});

const fetchTaskStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskLoading: true,
  taskCreateSuccess: false,
  taskCreateError: null,
});

const fetchTaskSuccess = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  task: action.task,
  taskLoading: false,
  taskError: null,
});

const fetchTaskFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  taskError: action.taskError,
  taskLoading: false,
});

const createTaskStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskCreateLoading: true,
  createdTask: null,
});

const createTaskSuccess = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  taskCreateLoading: false,
  taskCreateError: null,
  taskCreateSuccess: true,
  createdTask: action.createdTask,
});

const createTaskFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  taskCreateLoading: false,
  taskCreateError: action.taskCreateError,
});

const updateTaskStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskUpdateLoading: true,
  taskUpdateSuccess: false,
});

const updateTaskSuccess = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskUpdateLoading: false,
  taskUpdateError: null,
  taskUpdateSuccess: true,
});

const updateTaskFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  taskUpdateLoading: false,
  taskUpdateError: action.taskUpdateError,
});

const deleteTaskStart = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskDeleteLoading: true,
});

const deleteTaskSuccess = (state: TaskStateType): TaskStateType => ({
  ...state,
  taskDeleteLoading: false,
  taskDeleteError: null,
  taskDeleteSuccess: true,
  tasksListUpdateNeeded: true,
});

const deleteTaskFail = (
  state: TaskStateType,
  action: TaskActionType,
): TaskStateType => ({
  ...state,
  taskDeleteLoading: false,
  taskDeleteError: action.taskDeleteError,
});

const refreshTaskList = (state: TaskStateType): TaskStateType => ({
  ...initialState,
  tasksListUpdateNeeded: true,
});

const resetTaskStore = (state: TaskStateType): TaskStateType => ({
  ...initialState,
});

const completelyResetTaskStore = (): TaskStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: TaskActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_TASKS_START:
      return fetchTasksStart(state);
    case actionTypes.FETCH_TASKS_SUCCESS:
      return fetchTasksSuccess(state, action);
    case actionTypes.FETCH_TASKS_FAIL:
      return fetchTasksFail(state, action);
    case actionTypes.FETCH_ALL_TASKS_START:
      return fetchAllTasksStart(state);
    case actionTypes.FETCH_ALL_TASKS_SUCCESS:
      return fetchAllTasksSuccess(state, action);
    case actionTypes.FETCH_ALL_TASKS_FAIL:
      return fetchAllTasksFail(state, action);
    case actionTypes.FETCH_TASK_START:
      return fetchTaskStart(state);
    case actionTypes.FETCH_TASK_SUCCESS:
      return fetchTaskSuccess(state, action);
    case actionTypes.FETCH_TASK_FAIL:
      return fetchTaskFail(state, action);
    case actionTypes.CREATE_TASK_START:
      return createTaskStart(state);
    case actionTypes.CREATE_TASK_SUCCESS:
      return createTaskSuccess(state, action);
    case actionTypes.CREATE_TASK_FAIL:
      return createTaskFail(state, action);
    case actionTypes.UPDATE_TASK_START:
      return updateTaskStart(state);
    case actionTypes.UPDATE_TASK_SUCCESS:
      return updateTaskSuccess(state);
    case actionTypes.UPDATE_TASK_FAIL:
      return updateTaskFail(state, action);
    case actionTypes.DELETE_TASK_START:
      return deleteTaskStart(state);
    case actionTypes.DELETE_TASK_SUCCESS:
      return deleteTaskSuccess(state);
    case actionTypes.DELETE_TASK_FAIL:
      return deleteTaskFail(state, action);
    case actionTypes.REFRESH_TASK_LIST:
      return refreshTaskList(state);
    case actionTypes.RESET_TASK_STORE:
      return resetTaskStore(state);
    case actionTypes.LOGOUT:
      return completelyResetTaskStore();
    default:
      return state;
  }
};

export default reducer;
